import * as THREE from 'three';
import { resolveNavigationMode, validateCameraPose } from '../runtime/_pure/cameraNavigation.js';
import { horizontalYaw } from './_pure/xrLocomotion.js';
export function createCameraController(args) {
    let mode = 'free';
    let guided = false;
    let freePose;
    let freeNavigation = false;
    let freeGuidedPose;
    let lastConfiguration;
    let focusDistance = 16;
    let xrFov = 50;
    let xrDistanceScale = 1;
    let referenceIsXr = false;
    /** In XR the viewer moves freely; guided poses are tracked here instead of moving the rig. */
    let xrGuidedPose;
    let xrArrivalPending = false;
    let xrArrival;
    let xrArrivalSerial = 0;
    const neutralPosition = new THREE.Vector3();
    const neutralOrientation = new THREE.Quaternion();
    const captureHead = () => {
        const head = args.xrCamera?.();
        neutralPosition.copy(head?.position ?? new THREE.Vector3());
        neutralOrientation.copy(head?.quaternion ?? new THREE.Quaternion());
    };
    const recordArrival = () => {
        const rig = args.rig();
        if (!rig)
            return;
        const eye = neutralPosition.clone().applyQuaternion(rig.quaternion).add(rig.position);
        const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(rig.quaternion.clone().multiply(neutralOrientation));
        xrArrival = { serial: ++xrArrivalSerial, eye: eye.toArray(), forward: forward.toArray() };
    };
    const stagedPoses = new WeakMap();
    let latestStaged;
    const getMode = () => freePose || referenceIsXr ? 'free' : resolveNavigationMode(mode, guided);
    const syncControls = () => {
        const controls = args.controls();
        if (controls) {
            const free = getMode() === 'free';
            controls.enabled = free && !args.isXr();
            if (!free && controls.enableDamping) {
                controls.enableDamping = false;
                controls.update();
            }
            else {
                controls.enableDamping = free;
            }
        }
        args.onModeChange(getMode());
    };
    const apply = (pose) => {
        const camera = args.camera();
        const controls = args.controls();
        if (!camera || !controls)
            throw new Error('Camera controller is not ready.');
        if (args.isXr()) {
            const rig = args.rig();
            if (!rig)
                throw new Error('XR camera rig is not ready.');
            const direction = new THREE.Vector3(...pose.target).sub(new THREE.Vector3(...pose.position));
            focusDistance = direction.length();
            xrFov = pose.fov;
            const eye = args.xrCamera?.()?.cameras[0];
            const verticalFov = eye ? 2 * Math.atan(1 / eye.projectionMatrix.elements[5]) : undefined;
            xrDistanceScale = verticalFov ? Math.tan(THREE.MathUtils.degToRad(pose.fov) / 2) / Math.tan(verticalFov / 2) : 1;
            const headForward = new THREE.Vector3(0, 0, -1).applyQuaternion(neutralOrientation);
            rig.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), horizontalYaw(direction) - horizontalYaw(headForward));
            const eyePosition = new THREE.Vector3(...pose.target).addScaledVector(direction, -xrDistanceScale);
            rig.position.copy(eyePosition).sub(neutralPosition.clone().applyQuaternion(rig.quaternion));
            rig.updateMatrixWorld(true);
            return;
        }
        camera.position.set(...pose.position);
        controls.target.set(...pose.target);
        if (camera instanceof THREE.PerspectiveCamera) {
            camera.fov = pose.fov;
            camera.far = Math.max(2000, camera.position.distanceTo(controls.target) * 4);
            camera.updateProjectionMatrix();
        }
        controls.update();
    };
    const readPose = (xr) => {
        const camera = args.camera();
        const controls = args.controls();
        if (!camera || !controls)
            return null;
        const fov = camera instanceof THREE.PerspectiveCamera ? camera.fov : 50;
        if (xr) {
            const rig = args.rig();
            if (!rig)
                return null;
            const orientation = rig.quaternion.clone().multiply(neutralOrientation);
            const eye = neutralPosition.clone().applyQuaternion(rig.quaternion).add(rig.position);
            const direction = new THREE.Vector3(0, 0, -1).applyQuaternion(orientation);
            const target = eye.clone().addScaledVector(direction, focusDistance * xrDistanceScale);
            const position = target.clone().addScaledVector(direction, -focusDistance);
            return { position: position.toArray(), target: target.toArray(), fov: xrFov };
        }
        return { position: camera.position.toArray(), target: controls.target.toArray(), fov };
    };
    const place = (pose) => {
        if (referenceIsXr && xrArrivalPending) {
            // Arriving in a new scene places the viewer as if XR had been entered there.
            xrArrivalPending = false;
            captureHead();
            apply(pose);
            recordArrival();
        }
        if (referenceIsXr)
            xrGuidedPose = pose;
        else
            apply(pose);
    };
    return {
        syncReferenceFrame() {
            const xr = args.isXr();
            if (xr === referenceIsXr)
                return;
            const pose = latestStaged ?? xrGuidedPose ?? readPose(referenceIsXr);
            referenceIsXr = xr;
            xrGuidedPose = xr && pose ? pose : undefined;
            xrArrivalPending = false;
            xrArrival = undefined;
            if (xr)
                captureHead();
            // Entering XR starts the viewer at the guided viewpoint; leaving it returns the desktop there.
            if (pose)
                apply(pose);
            if (xr && pose)
                recordArrival();
            syncControls();
        },
        readXrArrival: () => xrArrival,
        recenterXr() {
            const pose = latestStaged ?? xrGuidedPose;
            if (!referenceIsXr || !args.isXr() || !pose)
                return false;
            captureHead();
            apply(pose);
            return true;
        },
        referenceFrame: () => args.isXr() ? 'xr' : 'desktop',
        ...(args.viewportAspect ? { viewportAspect: args.viewportAspect } : {}),
        getMode,
        setFree(active, behavior = 'inspection') {
            if (active === (freePose !== undefined))
                return;
            if (active) {
                const pose = this.read();
                if (!pose)
                    throw new Error('Free camera requires a ready camera controller.');
                freePose = { position: [...pose.position], target: [...pose.target], fov: pose.fov };
                freeNavigation = behavior === 'navigation';
                freeGuidedPose = freePose;
                syncControls();
            }
            else if (freePose) {
                const pose = freeNavigation ? latestStaged ?? freeGuidedPose ?? freePose : freePose;
                freePose = undefined;
                freeNavigation = false;
                freeGuidedPose = undefined;
                // Discard residual orbit damping before restoring the saved pose.
                const controls = args.controls();
                if (controls) {
                    controls.enableDamping = false;
                    controls.update();
                }
                place(pose);
                syncControls();
            }
        },
        setMode(nextMode) {
            mode = nextMode;
            syncControls();
        },
        setGuided(active) {
            if (guided === active)
                return;
            if (!active && latestStaged) {
                this.apply(latestStaged);
                latestStaged = undefined;
            }
            guided = active;
            syncControls();
        },
        stage(frame, pose) {
            stagedPoses.set(frame, pose);
            latestStaged = pose;
        },
        commitFrame(frame) {
            if (freePose && !freeNavigation)
                return;
            const pose = stagedPoses.get(frame);
            if (!pose)
                return;
            stagedPoses.delete(frame);
            this.apply(pose);
            if (latestStaged === pose)
                latestStaged = undefined;
        },
        read() {
            if (latestStaged)
                return latestStaged;
            if (freePose && freeNavigation)
                return freeGuidedPose ?? freePose;
            if (referenceIsXr && xrGuidedPose)
                return xrGuidedPose;
            return readPose(args.isXr());
        },
        apply(pose) {
            if (freePose) {
                if (freeNavigation)
                    freeGuidedPose = pose;
                return;
            }
            place(pose);
        },
        reset() {
            mode = 'free';
            guided = false;
            freePose = undefined;
            freeNavigation = false;
            freeGuidedPose = undefined;
            lastConfiguration = undefined;
            latestStaged = undefined;
            xrGuidedPose = undefined;
            xrArrivalPending = referenceIsXr;
            xrArrival = undefined;
            // Discard orbit momentum carried over from the previous scene.
            const controls = args.controls();
            if (controls?.enableDamping) {
                controls.enableDamping = false;
                controls.update();
            }
            syncControls();
        },
        configure(pose) {
            if (freePose && !freeNavigation)
                return;
            validateCameraPose(pose);
            const signature = JSON.stringify(pose);
            if (signature === lastConfiguration)
                return;
            if (guided)
                return;
            if (mode === 'guided' && lastConfiguration !== undefined)
                return;
            this.apply(pose);
            lastConfiguration = signature;
        },
    };
}
